import { SearchTruckerMessageError } from 'config/truckerProfileService';
import AppContext from 'context';
import { SUGGESTED_TAB } from 'context/reducers/tabs.type';
import { useContext, useMemo } from 'react';
import { useFilteredData } from '../useFilteredData';

export const useAppStates = () => {
  const {
    state: {
      truckers: { meta, truckerType, vehicles, areaCodes, sort },
      suggestedTruckers: { isFetchedSuggested, isFetchingSuggested, errorSuggested },
      searchTruckers: { isFetchingSearch, isFetchedSearch, errorSearch },
      tabs: { activeTab },
      refundableFare: { dataRefundableFare, isFetchedRefundableFare },
    },
  } = useContext(AppContext);

  const { filteredDataSuggested } = useFilteredData();

  const isLoadingFull = useMemo(() => !meta && isFetchingSuggested, [meta, isFetchingSuggested]);

  const isLoadingPartial = useMemo(
    () => meta && (isFetchingSuggested || isFetchingSearch),
    [meta, isFetchingSuggested, isFetchingSearch]
  );

  const isSuggestedTabActive = useMemo(() => activeTab === SUGGESTED_TAB, [activeTab]);

  const isLoadedSuggested = useMemo(
    () =>
      isSuggestedTabActive &&
      isFetchedSuggested &&
      !isFetchingSuggested &&
      !errorSuggested &&
      !!filteredDataSuggested?.length,
    [isSuggestedTabActive, isFetchedSuggested, isFetchingSuggested, errorSuggested, filteredDataSuggested]
  );

  const isEmptySuggested = useMemo(
    () =>
      isSuggestedTabActive &&
      isFetchedSuggested &&
      !isFetchingSuggested &&
      !errorSuggested &&
      !!!filteredDataSuggested?.length,
    [isSuggestedTabActive, isFetchedSuggested, isFetchingSuggested, errorSuggested, filteredDataSuggested]
  );

  const isErrorLoadSuggested = useMemo(
    () => isSuggestedTabActive && isFetchedSuggested && !isFetchingSuggested && errorSuggested,
    [isSuggestedTabActive, isFetchedSuggested, isFetchingSuggested, errorSuggested]
  );

  const isEmptySearchDriver = useMemo(
    () =>
      isFetchedSearch &&
      (errorSearch === SearchTruckerMessageError.notFound || errorSearch === SearchTruckerMessageError.duplicated),
    [isFetchedSearch, errorSearch]
  );

  const isLoadErrorFull = useMemo(() => !meta && isErrorLoadSuggested, [meta, isErrorLoadSuggested]);

  const isLoadErrorPartial = useMemo(() => meta && isErrorLoadSuggested, [meta, isErrorLoadSuggested]);

  const isFiltersApplied = useMemo(
    () => sort || truckerType?.length || vehicles?.length || areaCodes?.length,
    [truckerType, vehicles, areaCodes, sort]
  );

  const isDataLoaded = useMemo(() => isLoadedSuggested, [isLoadedSuggested]);

  const isEmptyRefundableFare = useMemo(
    () => isFetchedRefundableFare && !dataRefundableFare.length,
    [dataRefundableFare, isFetchedRefundableFare]
  );

  return {
    isLoadingFull,
    isLoadingPartial,
    isEmptySuggested,
    isLoadErrorFull,
    isLoadErrorPartial,
    isErrorLoadSuggested,
    isFiltersApplied,
    isDataLoaded,
    isEmptyRefundableFare,
    isEmptySearchDriver,
  };
};
